import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { t } from '../utils/translations';
import api from '../utils/api';
import { Bell, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_forfait-downgrade/artifacts/t6wkkpkw_Logo%20apollo%20sans%20baseline%20-%20f77f00.png";

/**
 * C3 - Hamburger animé ☰ ↔ X (transition 300ms)
 * Visible uniquement < lg (mobile/tablette). Sur desktop la sidebar est fixe, pas de hamburger.
 */
function AnimatedHamburger({ open }) {
  // 3 barres superposées. Quand open, celle du haut rotate 45°, celle du bas -45°, celle du milieu fade.
  const barBase = 'absolute left-0 h-[2px] w-6 rounded-full transition-all duration-300 ease-in-out';
  return (
    <span className="relative inline-block w-6 h-6" aria-hidden="true">
      <span className={barBase} style={{ background: '#F77F00', top: open ? '11px' : '6px', transform: open ? 'rotate(45deg)' : 'rotate(0)' }} />
      <span className={barBase} style={{ background: '#F77F00', top: '11px', opacity: open ? 0 : 1 }} />
      <span className={barBase} style={{ background: '#F77F00', top: open ? '11px' : '16px', transform: open ? 'rotate(-45deg)' : 'rotate(0)' }} />
    </span>
  );
}

export default function Header({ drawerOpen = false, onToggleDrawer }) {
  const { user, logout } = useAuth();
  const { lang, setLanguage } = useLang();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const { data } = await api.get('/notifications/unread-count');
        setUnreadCount(data.count);
      } catch {}
    };
    fetchCount();
    const interval = setInterval(fetchCount, 15000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const { data } = await api.get('/notifications');
      setNotifications(data);
    } catch {}
  };

  const handleBellClick = () => {
    if (!showNotifs) fetchNotifications();
    setShowNotifs(!showNotifs);
  };

  const markAllRead = async () => {
    try {
      await api.put('/notifications/read', { notification_ids: [] });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  };

  const handleNotifClick = async (notif) => {
    if (!notif.read) {
      await api.put('/notifications/read', { notification_ids: [notif._id] });
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    setShowNotifs(false);
    if (notif.ticket_id) navigate(`/tickets/${notif.ticket_id}`);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const roleLabels = { member: 'Membre', consultant: 'Consultant', dr: 'DR', dn: 'DN', direction: 'Direction', support: 'Support' };

  return (
    <header data-testid="app-header" className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between pl-3 pr-3 sm:pl-4 sm:pr-4 lg:px-6 py-2" style={{ background: '#0F0F1A', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
      {/* Left group : hamburger (mobile) + logo */}
      <div className="flex items-center gap-2 min-w-0">
        {onToggleDrawer && (
          <button
            data-testid="menu-hamburger"
            onClick={onToggleDrawer}
            className="lg:hidden p-2 rounded-lg hover:bg-white/5 flex-shrink-0"
            aria-label={drawerOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={drawerOpen}
            style={{ minWidth: '44px', minHeight: '44px' }}
          >
            <AnimatedHamburger open={drawerOpen} />
          </button>
        )}
        <div className="flex items-center gap-2 cursor-pointer min-w-0" data-testid="apollo-logo" onClick={() => navigate('/')}>
          <img src={LOGO_URL} alt="Apollo" className="h-7 sm:h-8 object-contain flex-shrink-0" />
          <span className="hidden sm:inline text-lg" style={{ color: '#6B7280' }}>/</span>
          <span className="hidden sm:inline text-lg font-medium truncate" style={{ color: '#6B7280' }}>ticketing</span>
        </div>
      </div>

      {/* Right group */}
      <div className="flex items-center gap-1 sm:gap-3 lg:gap-4 flex-shrink-0">
        {/* Role badge — caché < sm */}
        <span data-testid="role-badge" className="hidden sm:inline text-xs px-3 py-1 rounded-full whitespace-nowrap" style={{ background: 'rgba(247,127,0,0.15)', color: '#F77F00' }}>
          {roleLabels[user?.role] || user?.role}
        </span>

        {/* Language Toggle — toujours visible */}
        <div data-testid="language-toggle" className="flex rounded-full overflow-hidden flex-shrink-0" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
          <button
            data-testid="lang-fr-btn"
            onClick={() => setLanguage('fr')}
            className="px-3 py-1.5 text-xs font-semibold transition-all duration-200"
            style={{ background: lang === 'fr' ? '#F77F00' : 'transparent', color: lang === 'fr' ? '#fff' : '#6B7280' }}
          >FR</button>
          <button
            data-testid="lang-en-btn"
            onClick={() => setLanguage('en')}
            className="px-3 py-1.5 text-xs font-semibold transition-all duration-200"
            style={{ background: lang === 'en' ? '#F77F00' : 'transparent', color: lang === 'en' ? '#fff' : '#6B7280' }}
          >EN</button>
        </div>

        {/* Notifications */}
        <div className="relative">
          <button
            data-testid="notifications-bell"
            onClick={handleBellClick}
            className="relative p-2 rounded-lg transition-colors hover:bg-white/5"
            style={{ minWidth: '44px', minHeight: '44px' }}
          >
            <Bell size={20} style={{ color: '#F77F00' }} />
            {unreadCount > 0 && (
              <span className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center text-white" style={{ background: '#F77F00' }}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {showNotifs && (
            <div
              data-testid="notifications-dropdown"
              className="absolute right-0 top-12 rounded-xl shadow-2xl overflow-hidden animate-fade-in"
              style={{
                background: '#1A1A2E',
                border: '1px solid rgba(255,255,255,0.08)',
                width: 'min(calc(100vw - 24px), 320px)',
              }}
            >
              <div className="flex items-center justify-between p-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <span className="font-semibold text-sm">{t('notifications', lang)}</span>
                <button onClick={markAllRead} className="text-xs" style={{ color: '#F77F00' }}>{t('mark_all_read', lang)}</button>
              </div>
              <div className="max-h-72 overflow-y-auto">
                {notifications.length === 0 ? (
                  <p className="text-center py-6 text-sm" style={{ color: '#6B7280' }}>{t('no_notifications', lang)}</p>
                ) : notifications.map(n => (
                  <button key={n._id} data-testid={`notification-${n._id}`} onClick={() => handleNotifClick(n)}
                    className="w-full text-left px-4 py-3 hover:bg-white/5 transition-colors"
                    style={{
                      borderBottom: '1px solid rgba(255,255,255,0.04)',
                      opacity: n.read ? 0.6 : 1,
                      background: n.type === 'sla_breach' && !n.read ? 'rgba(239,68,68,0.06)' : 'transparent'
                    }}>
                    <div className="flex items-center gap-2">
                      {n.type === 'sla_breach' && <span style={{ color: '#EF4444', fontSize: 14 }}>&#9888;</span>}
                      <p className="text-sm" style={{ color: n.type === 'sla_breach' ? '#EF4444' : '#E8E8E8' }}>{lang === 'fr' ? n.message_fr : n.message_en}</p>
                    </div>
                    <p className="text-xs mt-1" style={{ color: '#6B7280' }}>{new Date(n.created_at).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US')}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Logout — icône seule < sm, texte + icône sm+ */}
        <button
          data-testid="logout-button"
          onClick={handleLogout}
          className="flex items-center gap-2 text-sm px-2 sm:px-3 py-1.5 rounded-lg transition-colors hover:bg-white/5"
          style={{ color: '#6B7280', minHeight: '44px' }}
          aria-label={t('logout', lang)}
        >
          <span className="hidden sm:inline">{t('logout', lang)}</span>
          <LogOut size={18} />
        </button>
      </div>
    </header>
  );
}
